import { substituteCmsUrlTokens } from '@hyphengroup/cms-components/utils/substituteCmsUrlTokens';
import { getAllSearchParams, safeUrl } from 'utils/uri';
import { ThrowableProblem } from '../../../../../../../../../utils/Problem';
import { LeadData } from './types';

/**
 * if you are on https://moneymax.ph/apply?param1=value1&param2=value2
 * and redirect url https://moneymax.ph/redirect?nextParam1={param1}&nextParam2={param2}
 * if key in param={key} doesn't exist in original url, it will be skipped
 * if we have a constant param like: param=value in redirect url it stays
 * this function will compose an url to redirect to based on current search params: https://moneymax.ph/redirect?nextParam1=value1&nextParam2=value2
 * @param redirectLink
 */
export function passUrlParamsForRedirect(
  currentUrl: string,
  redirectLink: string,
) {
  if (!redirectLink) throw new Error(`No url to redirect to`);
  const url = safeUrl(currentUrl);
  const redirectUrl = safeUrl(redirectLink);

  if (!url || !redirectUrl) {
    throw new Error(`Invalid redirect url`);
  }

  const currentSearchParams = getAllSearchParams(url.searchParams);
  const redirectParams = getAllSearchParams(redirectUrl.searchParams);
  const searchParamsKeys = Object.keys(currentSearchParams);
  for (const [key, values] of Object.entries(redirectParams)) {
    let nextValue = values.toString();
    if (!/{.*?}/.test(nextValue)) continue;
    if (!searchParamsKeys.some((orKey) => nextValue.includes(orKey))) {
      redirectUrl.searchParams.delete(key);
      continue;
    }
    searchParamsKeys.forEach(
      (orKey) =>
        (nextValue = nextValue.replace(
          `{${orKey}}`,
          currentSearchParams[orKey].toString(),
        )),
    );
    redirectUrl.searchParams.set(key, nextValue);
  }
  return redirectUrl;
}

/**
 * Resolves a provider redirect URL in two stages:
 * 1. Strapi-token substitution (`[payloadProductId]`, `[forwardParameters]`).
 * 2. The pre-existing template-substitution loop in `passUrlParamsForRedirect`
 *    (replaces `{paramName}` placeholders in the redirect URL with values from
 *    the current page's query string).
 *
 * The order is load-bearing: token substitution must run before any
 * `new URL()` parse, otherwise `[` / `]` get percent-encoded and the literal
 * token replace no longer matches.
 *
 * Note: the CMS-1689 deeplink-forward whitelist (productId / criteria /
 * catalogue-version) lives on a sibling branch and is not part of this PR. It
 * extends `passUrlParamsForRedirect` once both branches converge on master.
 */
export function resolveProviderRedirectUrl({
  redirectLink,
  productId,
  currentHref,
}: {
  redirectLink: string;
  productId?: string;
  currentHref: string;
}): URL {
  const withTokens = substituteCmsUrlTokens(redirectLink, {
    productId,
    currentSearch: new URL(currentHref).search,
  });
  return passUrlParamsForRedirect(currentHref, withTokens);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRedirectCta(links: any[], isMobile: boolean) {
  if (!links) return null;

  // Mobile: prioritize MOBILE, then fallback to regular
  if (isMobile) {
    return (
      links.find(
        (l) => l.trackingSourceType === 'PROVIDER_APPLICATION_FORM_MOBILE',
      ) ||
      links.find((l) => l.trackingSourceType === 'PROVIDER_APPLICATION_FORM')
    );
  }

  // Desktop: always use regular
  return links.find(
    (l) => l.trackingSourceType === 'PROVIDER_APPLICATION_FORM',
  );
}

// Literal space (not \s) so tabs/Unicode whitespace can't pass server-side
// validation into the Zoho payload, matching the client-side sanitizer/pattern.
const nameRegex = /^([\p{L}\p{M}'\d -]{1,100})$/u;

// The lead form's mobile input is `09`-prefixed and masked to 9 further digits,
// so a well-formed submission always reaches the server as exactly 11 digits.
const phMobileRegex = /^09\d{9}$/;

// Deliberately permissive: one @, no whitespace, a dotted domain. The field is
// already `type="email"` with a stricter pattern client-side; this is the
// server-side floor that stops an empty or obviously malformed address from
// reaching Zoho, not a second opinion on RFC 5322.
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * CORE-4728: BBB is SME broadband (the code avoids `BB`, already used by
 * broadband in another MHG market) and `CVG` is the provider, Converge.
 *
 * Selection is on the vertical+provider pair `BBB.CVG`, not the vertical alone,
 * so a future BBB product from another provider keeps the default form until
 * someone decides it should not.
 *
 * Match the pair wherever it sits rather than anchoring at segment 0: the URL
 * carries the unprefixed code (`/start/BBB.CVG.MICRO-BIZ`) while the product
 * uid returned by GraphQL carries a country prefix (`PH.BBB.CVG.MICRO-BIZ`).
 * Both must select the same template.
 */
export function isBbbShortFormProduct(productUid?: string | null): boolean {
  if (!productUid) return false;
  return /^(?:[A-Za-z]{2}\.)?BBB\.CVG\..+/.test(productUid);
}

/**
 * Zoho's `Product_UID` stores the uid without the market prefix —
 * `BBB.CVG.MICRO-BIZ`, not `PH.BBB.CVG.MICRO-BIZ` — matching the form the URL
 * already uses.
 *
 * Precondition: callers pass the uid from the GraphQL product, which always
 * carries the two-letter country segment. The strip is conditional rather than
 * "drop the first segment" so an already-unprefixed uid passes through
 * unchanged — but note it cannot tell a country code from a two-letter vertical
 * code, so an unprefixed `CC.SOME-CARD` would lose its `CC`. That shape does
 * not reach here today; if it ever can, match against the market list instead.
 */
export function toZohoProductUid(productUid?: string | null): string {
  if (!productUid) return '';
  return productUid.replace(/^[A-Za-z]{2}\./, '');
}

/**
 * `productUid` is passed so the server, not the caller, decides which rules
 * apply — the client supplies a fact, the rule lives here.
 */
export function validateLeadInput(leadData: LeadData, productUid?: string) {
  let errorMessage = '';
  if (leadData.First_Name && !nameRegex.test(leadData.First_Name)) {
    errorMessage += 'First name is invalid. ';
  }
  // "N/A" is the sentinel written when the user ticks "I don't have a Middle
  // Name" (CORE-4532). It must reach Zoho's Middle_Name as-is, so whitelist it
  // here even though the "/" would otherwise fail nameRegex.
  if (
    leadData.Middle_Name &&
    leadData.Middle_Name !== 'N/A' &&
    !nameRegex.test(leadData.Middle_Name)
  ) {
    errorMessage += 'Middle name is invalid. ';
  }
  if (leadData.Last_Name && !nameRegex.test(leadData.Last_Name)) {
    errorMessage += 'Last name is invalid. ';
  }

  // Scoped to the BBB short form on purpose. `validateLeadInput` also guards the
  // car-insurance leads that reach Zoho through `SendLeadToZoho`, and those run
  // on SingSaver (SG) too — a blanket PH mobile rule would reject them.
  if (isBbbShortFormProduct(productUid)) {
    if (!phMobileRegex.test(leadData.Phone ?? '')) {
      errorMessage += 'Mobile number is invalid. ';
    }
    if (!emailRegex.test(leadData.Email ?? '')) {
      errorMessage += 'Email is invalid. ';
    }
  }

  if (errorMessage) {
    throw new ThrowableProblem({
      type: 'validation-error',
      title: 'Lead input validation error',
      status: 400,
      detail: errorMessage.trim(),
    });
  }
}

export function isInternalDomain(url: string): boolean {
  const internalDomains = [
    'moneymax.ph',
    'singsaver.com.sg',
    'moneyhero.com.hk',
    'money101.com.tw',
    'moneyherogroup.com',
    'mhg-staging.com',
  ];
  try {
    const redirectUrl = new URL(url);
    return internalDomains.some((domain) =>
      redirectUrl.hostname.endsWith(domain),
    );
  } catch (e) {
    return false;
  }
}
